// import components
import { Box } from '@mui/material';
import Header from '../Header';
import EmployeeGrid from './EmployeeGrid/EmployeeGrid';
import HeaderFilters from './HeaderFilters';
import OpenRequestsGrid from './OpenRequestsGrid/OpenRequestsGrid';
import RosterViewByEmployeeStyles from './RosterViewByEmployee.styles';

// import hooks
import { useSelector } from 'hooks';
import { useEffect, useRef, useState } from 'react';

// import utils
import { ScrollbarBox } from '@sdb/planning-ui';
import classNames from 'classnames';
import { values } from 'lodash';
import { getMonthInterval } from 'utils/date';
import { printContent } from 'utils/misc';
import { getRosterDays, prepareRosterForPrint } from '../utils';

// import types
import type { Interval } from 'components/types';
import type { Employee } from './EmployeeGrid/types';
import type { FilterOption, RefProps } from './HeaderFilters';
import type { OpenRequestsRow } from './OpenRequestsGrid/types';
import type { RosterViewByEmployeeProps } from './RosterViewByEmployee.types';

export default function RosterViewByEmployee({
  portal,
  isAccordion,
  getData,
  selectedPlanUnits,
  lastSelectedPlanUnitsIds,
  onPlanUnitSelected,
  showOnlyPlanned,
  onShowOnlyPlanned,
  defaultInterval,
  onRosterViewChanged
}: RosterViewByEmployeeProps) {
  'use memo';

  const [interval, setInterval] = useState(
    defaultInterval || getMonthInterval()
  );

  const holidays = useSelector((state) => state.holidays);
  const [rosterDays, setRosterDays] = useState(
    getRosterDays(interval, holidays)
  );
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filterOption, setFilterOption] = useState<FilterOption | null>(null);
  const [openRequestsRows, setOpenRequestsRows] = useState<OpenRequestsRow[]>(
    []
  );
  const rosterRef = useRef(document.createElement('div'));
  const headerFiltersRef = useRef<RefProps>({} as RefProps);

  const onIntervalChanged = (interval: Interval) => {
    setInterval(interval);
    setRosterDays(getRosterDays(interval, holidays));
  };

  const employeesByPlanned = showOnlyPlanned
    ? employees.filter((e) => values(e.days).flat().length)
    : employees;

  const filteredEmployees = employeesByPlanned.filter((e) => {
    if (filterOption) {
      if (filterOption.type === 'name') {
        return e.nameInformal === filterOption.label;
      }
      if (filterOption.type === 'function') {
        return e.benefit.function?.name === filterOption.label;
      }
      if (filterOption.type === 'planunit') {
        return (
          e.benefit.costCenter?.name === filterOption.label ||
          e.plannedCostCenter.name === filterOption.label
        );
      }
    }
    return true;
  });

  useEffect(() => {
    setEmployees([]);
    setOpenRequestsRows([]);
    if (selectedPlanUnits.length) {
      getData(interval, selectedPlanUnits).then(
        ({ employees, openRequestsRows }) => {
          setEmployees(employees);
          setOpenRequestsRows(openRequestsRows);
        }
      );
    }
  }, [getData, interval, selectedPlanUnits]);

  useEffect(() => {
    headerFiltersRef.current.clearSearchFilter();
  }, [selectedPlanUnits]);

  return (
    <ScrollbarBox>
      <RosterViewByEmployeeStyles
        className={classNames({ 'is-accordion': isAccordion })}
        ref={rosterRef}
      >
        <Header
          lastSelectedPlanUnitsIds={lastSelectedPlanUnitsIds}
          onPlanUnitSelected={onPlanUnitSelected}
          onPrintRoster={() => {
            printContent(prepareRosterForPrint(rosterRef.current));
          }}
          interval={interval}
          onIntervalChanged={onIntervalChanged}
          rosterDays={rosterDays}
          calendarView="month"
          rosterView="employees"
          onRosterViewChanged={onRosterViewChanged}
        >
          <HeaderFilters
            employees={employeesByPlanned}
            showOnlyPlanned={showOnlyPlanned}
            onShowOnlyPlanned={onShowOnlyPlanned}
            onFilterOptionSelected={setFilterOption}
            ref={headerFiltersRef}
          />
        </Header>
        <div className="roster-body">
          <Box className="wrap-container">
            <EmployeeGrid
              employees={filteredEmployees}
              isAccordion={isAccordion}
              selectedPlanUnits={selectedPlanUnits}
              rosterDays={rosterDays}
              portal={portal}
              interval={interval}
            />
            <OpenRequestsGrid
              openRequestsRows={openRequestsRows}
              isAccordion={isAccordion}
              selectedPlanUnits={selectedPlanUnits}
              rosterDays={rosterDays}
              portal={portal}
            />
          </Box>
        </div>
      </RosterViewByEmployeeStyles>
    </ScrollbarBox>
  );
}
